"use client";

import { Flame, Home, LayoutGrid, ShoppingCart, User } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";

import { useCartStore } from "@/components/providers/cart-store";
import { cn } from "@/lib/utils";

export function MobileBottomNav() {
  const pathname = usePathname();
  const { data: session } = useSession();
  const cartCount = useCartStore((s) => s.count());
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const displayCartCount = mounted ? cartCount : 0;

  // Do not show bottom nav on checkout, cart, admin, or individual product detail pages (where the product action bar is active)
  if (
    pathname?.startsWith("/checkout") ||
    pathname === "/cart" ||
    pathname?.startsWith("/cart") ||
    pathname?.startsWith("/admin") ||
    (pathname?.startsWith("/products/") && pathname !== "/products")
  ) {
    return null;
  }

  const navItems = [
    {
      label: "Home",
      href: "/",
      icon: Home,
      isActive: pathname === "/",
    },
    {
      label: "Categories",
      href: "/search",
      icon: LayoutGrid,
      isActive: pathname === "/search" && !pathname.includes("deal"),
    },
    {
      label: "Deals",
      href: "/search?deal=flash",
      icon: Flame,
      isActive: pathname?.includes("deal"),
      highlight: true,
    },
    {
      label: "Cart",
      href: "/cart",
      icon: ShoppingCart,
      isActive: pathname === "/cart",
      badge: displayCartCount,
    },
    {
      label: session ? "Account" : "Sign In",
      href: session
        ? session.user.role === "VENDOR"
          ? "/vendor"
          : session.user.role === "ADMIN"
          ? "/admin"
          : session.user.role === "ADMIN1"
          ? "/admin1"
          : "/dashboard/profile"
        : "/login",
      icon: User,
      isActive: pathname?.startsWith("/dashboard") || pathname?.startsWith("/login"),
    },
  ];

  return (
    <nav
      aria-label="Mobile Navigation"
      className="fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border-t border-slate-200/90 dark:border-white/10 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] sm:hidden pb-[env(safe-area-inset-bottom)]"
    >
      <div className="grid grid-cols-5 h-14">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = item.isActive;

          return (
            <Link
              key={item.label}
              href={item.href}
              className={cn(
                "relative flex flex-col items-center justify-center gap-0.5 text-[10px] font-bold transition-colors select-none active:scale-95",
                active
                  ? "text-[#087DB5] dark:text-[#00B7B0]"
                  : item.highlight
                  ? "text-[#FF8A00]"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              )}
            >
              <div className="relative">
                <Icon
                  className={cn(
                    "h-5 w-5 transition-transform",
                    active && "scale-110",
                    item.highlight && "text-[#FF8A00]"
                  )}
                  strokeWidth={active ? 2.5 : 2}
                />
                {typeof item.badge === "number" && item.badge > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[17px] h-4 px-1 rounded-full bg-[#FF8A00] flex items-center justify-center text-[10px] font-bold text-white shadow-2xs leading-none">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </div>
              <span className={cn("truncate max-w-[64px]", active && "font-bold")}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
